import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function WesternCapePage() {
  useEffect(() => {
    document.title = "Western Cape Painters | Professional Painting Services Across the Region | Quality Workmanship";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services across the Western Cape. Expert interior & exterior painting, commercial services, and waterproofing throughout Cape Town and surrounding areas.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Western Cape painters, Cape Town painting services, regional painting contractors, Western Cape homes, professional painters');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/western-cape`);
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Western Cape Painters - Professional Painting Services",
      "description": "Professional painting services across the Western Cape region with quality finishes and expert craftsmanship.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/western-cape`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Western Cape",
        "description": "Professional painting services throughout the Western Cape region.",
        "areaServed": "Western Cape, South Africa",
        "serviceType": "Professional Regional Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Western Cape')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Painting',
      description: 'Comprehensive interior and exterior painting services for homes across the Western Cape. From coastal properties to inland estates, we deliver quality finishes.',
      price: 'From R8,000',
      features: ['Interior painting', 'Exterior painting', 'Color consultation', 'Premium materials', 'Regional coverage']
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Painting',
      description: 'Professional painting solutions for businesses throughout the Western Cape. Offices, retail spaces, and industrial properties with flexible scheduling.',
      price: 'From R12,000',
      features: ['Office complexes', 'Retail centers', 'Industrial facilities', 'Multi-site projects', 'Project management']
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Specialized Services',
      description: 'Expert roof painting, waterproofing, and protective coatings designed for the Western Cape\'s diverse climate conditions and property types.',
      price: 'From R6,000',
      features: ['Roof painting', 'Waterproofing', 'Protective coatings', 'Climate-specific solutions', 'Long-term protection']
    }
  ];

  const coverageAreas = [
    {
      title: 'Cape Town Metro',
      description: 'Comprehensive coverage across Cape Town including Atlantic Seaboard, Southern Suburbs, Northern Suburbs, and City Bowl areas with specialized teams for each region.',
      areas: ['Camps Bay', 'Clifton', 'Sea Point', 'Green Point', 'Constantia', 'Claremont', 'Newlands', 'Rondebosch']
    },
    {
      title: 'Northern Suburbs',
      description: 'Expert painting services throughout the Northern Suburbs including Durbanville, Bellville, Parow, and surrounding residential and commercial areas.',
      areas: ['Durbanville', 'Bellville', 'Parow', 'Goodwood', 'Bothasig', 'Edgemead', 'Milnerton', 'Table View']
    },
    {
      title: 'Coastal Areas',
      description: 'Specialized coastal painting with marine-grade protection for properties along the Western Cape coastline from Bloubergstrand to Hermanus.',
      areas: ['Bloubergstrand', 'Melkbosstrand', 'Big Bay', 'Hout Bay', 'Hermanus', 'Atlantic Beach']
    },
    {
      title: 'Wine Lands & Beyond',
      description: 'Quality painting services extending to the beautiful Winelands region and surrounding towns including Paarl, Stellenbosch, and Franschhoek.',
      areas: ['Paarl', 'Stellenbosch', 'Franschhoek', 'Somerset West', 'Val de Vie', 'George']
    }
  ];

  const features = [
    {
      title: 'Regional Expertise',
      description: 'Deep understanding of the Western Cape\'s diverse climate conditions, from coastal salt air to inland heat, ensuring appropriate materials and techniques for each location.'
    },
    {
      title: 'Quality Assurance',
      description: 'Consistent quality standards across all Western Cape projects with experienced teams, premium materials, and comprehensive quality control processes.'
    },
    {
      title: 'Flexible Service',
      description: 'Adaptable scheduling and project management to serve properties throughout the Western Cape, from single homes to multi-site commercial projects.'
    },
    {
      title: 'Local Knowledge',
      description: 'Extensive experience working across the Western Cape\'s unique environments, understanding local building styles, weather patterns, and property requirements.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Regional Assessment',
      description: 'Comprehensive property evaluation considering your specific Western Cape location, climate factors, and property requirements with detailed quotation.'
    },
    {
      step: 2,
      title: 'Preparation & Planning',
      description: 'Thorough surface preparation using location-appropriate techniques and materials. Coordinated scheduling for projects across multiple Western Cape sites.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional painting by experienced teams using quality materials suited to your specific Western Cape location and environmental conditions.'
    },
    {
      step: 4,
      title: 'Quality Verification',
      description: 'Comprehensive inspection ensuring consistent quality standards across all Western Cape projects with complete satisfaction guarantee.'
    }
  ];

  const testimonials = [
    {
      name: 'James Robertson',
      property: 'Cape Town Estate',
      rating: 5,
      text: 'Excellent service across multiple properties in the Western Cape. The team handled everything professionally from Cape Town to our Paarl property. Consistent quality and reliable service throughout.',
      image: 'https://public.readdy.ai/ai/img_res/b3745dfeb99be62bb128c6709a7b3d3a.jpg'
    },
    {
      name: 'Linda Botha',
      property: 'Coastal Home',
      rating: 5,
      text: 'Outstanding work on our coastal property. They understood the specific challenges of our location and used appropriate materials. The finish has held up beautifully against the elements.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20at%20Western%20Cape%20coastal%20property%2C%20professionally%20painted%20home%2C%20satisfied%20client%20portrait%2C%20seaside%20residence&width=80&height=80&seq=westerncape2&orientation=squarish'
    },
    {
      name: 'Peter Daniels',
      property: 'Commercial Portfolio',
      rating: 5,
      text: 'Managed our commercial properties across the Western Cape efficiently. Great project management, consistent quality, and minimal disruption to our business operations. Highly recommended.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20owner%20with%20Western%20Cape%20commercial%20properties%2C%20newly%20painted%20buildings%2C%20satisfied%20customer%2C%20quality%20commercial%20real%20estate&width=80&height=80&seq=westerncape3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Cape Town Residential Complex',
      description: 'Large-scale residential project in Cape Town with coordinated color schemes and quality finishes across multiple units. Demonstrates our capability in major developments.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20residential%20complex%20in%20Cape%20Town%20with%20Table%20Mountain%20backdrop%2C%20freshly%20painted%20exteriors%2C%20quality%20development%2C%20professional%20painting%20work&width=400&height=300&seq=westerncape4&orientation=landscape'
    },
    {
      title: 'Coastal Property Portfolio',
      description: 'Multiple coastal properties from Bloubergstrand to Hermanus with specialized marine-grade protection systems. Showcases our coastal painting expertise.',
      image: 'https://readdy.ai/api/search-image?query=Western%20Cape%20coastal%20properties%20with%20ocean%20views%2C%20newly%20painted%20exteriors%2C%20seaside%20homes%2C%20professional%20coastal%20painting%20finish&width=400&height=300&seq=westerncape5&orientation=landscape'
    },
    {
      title: 'Winelands Estate',
      description: 'Prestigious wine estate property in the Winelands with premium finishes and architectural detailing. Exemplifies our work in the region\'s luxury properties.',
      image: 'https://readdy.ai/api/search-image?query=Wine%20estate%20property%20in%20Western%20Cape%20Winelands%2C%20professionally%20painted%20exterior%2C%20luxury%20rural%20property%2C%20vineyard%20backdrop%2C%20quality%20finish&width=400&height=300&seq=westerncape6&orientation=landscape'
    },
    {
      title: 'Commercial Office Park',
      description: 'Multi-building commercial project with coordinated exterior scheme and efficient project management. Demonstrates our commercial painting capabilities.',
      image: 'https://readdy.ai/api/search-image?query=Western%20Cape%20commercial%20office%20park%2C%20professional%20exterior%20painting%2C%20modern%20business%20complex%2C%20quality%20commercial%20development&width=400&height=300&seq=westerncape7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20across%20Western%20Cape%20region%20with%20Table%20Mountain%20backdrop%2C%20diverse%20properties%2C%20painting%20equipment%2C%20quality%20workmanship%2C%20beautiful%20Cape%20Town%20landscape&width=1920&height=1080&seq=westerncape_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Western Cape
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Quality painting services across the Western Cape region. Expert craftsmanship for residential and commercial properties throughout Cape Town and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Western Cape Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Serving the Entire Western Cape Region
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                The Western Cape is South Africa's most diverse province, featuring stunning coastlines, majestic mountains, fertile winelands, and vibrant urban centers. This beautiful region demands painting services that understand its unique environmental challenges and architectural diversity.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our experienced teams serve properties throughout the Western Cape, from Cape Town's Atlantic Seaboard to the Garden Route, from the Winelands to coastal towns. We understand the specific requirements of each area and use appropriate materials and techniques.
              </p>
              <p className="text-lg text-gray-600">
                With regional expertise and consistent quality standards, we deliver professional painting services that enhance and protect properties across the Western Cape, whether residential estates, commercial developments, or specialized projects.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Western%20Cape%20landscape%20with%20Table%20Mountain%2C%20diverse%20regions%2C%20coastal%20areas%2C%20winelands%2C%20beautiful%20South%20African%20province%2C%20regional%20diversity&width=600&height=400&seq=westerncape8&orientation=landscape"
                alt="Western Cape Region"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Western Cape Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions for properties throughout the Western Cape region with consistent quality and professional service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Western Cape Coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional painting services across all major Western Cape regions with local expertise and consistent quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coverageAreas.map((area, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{area.title}</h3>
                <p className="text-gray-600 mb-4">{area.description}</p>
                <div className="flex flex-wrap gap-2">
                  {area.areas.map((location, idx) => (
                    <span key={idx} className="bg-yellow-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                      {location}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Western Cape Service
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Regional expertise combined with consistent quality standards for all Western Cape painting projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Regional Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring quality results across all Western Cape locations and property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Western Cape Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our painting projects across the diverse Western Cape region from coast to winelands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied property owners across the Western Cape region.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Western Cape Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact the Western Cape's trusted painting professionals. Get a free quote for your property anywhere in the region.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
